import type { CourseCertificateEvidenceModel } from "@/api/cle-api";

import {
  getCleFieldValidationState,
  getUnmatchedFailingFields,
  withApprovalRequiringValue,
  type CleFieldValidationState,
} from "./cleFieldValidation";

// Still used by the teaching, authorship and dispensation views, which have no
// backend fieldValidation at all and validate locally.
export const hasTextValue = (value: string | null | undefined): boolean =>
  value != null && value.trim() !== "";

export const isLegalCourseCertificateValid = (value: boolean | null): boolean =>
  value === true;

export const isSignatureValid = (value: boolean | null): boolean =>
  value === true;

export const isNumberOfLessonsValid = (value: number | null): boolean =>
  value != null;

export const isCourseDateValid = (value: string | null | undefined): boolean =>
  value != null && value !== "";

/**
 * Field keys the certificate validation card renders a row for. Anything else
 * the API rejects is surfaced by `getCourseCertificateUnmatchedFailingFields`.
 */
export const COURSE_CERTIFICATE_FIELD_KEYS = [
  "isLegalCourseCertificate",
  "courseTitle",
  "courseProvider",
  "participantName",
  "courseDate",
  "coursePeriod",
  "courseTime",
  "numberOfLessons",
  "signature",
  "courseDescription",
] as const;

type CourseCertificateFieldKey = (typeof COURSE_CERTIFICATE_FIELD_KEYS)[number];

/**
 * Whether the card has a value to print for each field. An approved verdict on
 * a field with no value cannot be trusted — see `withApprovalRequiringValue`.
 */
const FIELD_HAS_VALUE: Record<
  CourseCertificateFieldKey,
  (certificate: CourseCertificateEvidenceModel) => boolean
> = {
  isLegalCourseCertificate: (c) => c.isLegalCourseCertificate != null,
  courseTitle: (c) => hasTextValue(c.courseTitle),
  courseProvider: (c) => hasTextValue(c.courseProvider),
  participantName: (c) => hasTextValue(c.participantName),
  courseDate: (c) => isCourseDateValid(c.courseDate),
  // Kursusperioden printes ud fra startdatoen; slutdatoen er valgfri, fordi et
  // endagskursus ikke har en.
  coursePeriod: (c) => isCourseDateValid(c.courseDate),
  courseTime: (c) => c.courseTime != null,
  numberOfLessons: (c) => c.numberOfLessons != null,
  // Kriterie 3: underskrift ELLER udbyderens logo. Logoet tæller kun som sandt —
  // AI'en skriver false på hver kørsel, så dets blotte tilstedeværelse siger intet.
  signature: (c) => c.signature != null || c.providerLogo === true,
  courseDescription: (c) => hasTextValue(c.courseDescription),
};

export const getValidationFieldStatus = (
  certificate: CourseCertificateEvidenceModel,
): Record<CourseCertificateFieldKey, CleFieldValidationState> => {
  const { fieldValidation } = certificate;

  return Object.fromEntries(
    COURSE_CERTIFICATE_FIELD_KEYS.map((key) => [
      key,
      withApprovalRequiringValue(
        getCleFieldValidationState(fieldValidation, key),
        FIELD_HAS_VALUE[key](certificate),
      ),
    ]),
  ) as Record<CourseCertificateFieldKey, CleFieldValidationState>;
};

/**
 * `courseTitle` has no backend validation rule, so the API never returns a
 * verdict for it and the row rendered without an icon. Fall back to a green
 * check when a title is present; an absent title still shows no icon rather
 * than a fabricated failure (see getCleFieldValidationState).
 */
export const getCourseTitleFieldStatus = (
  state: CleFieldValidationState,
  courseTitle: string | null | undefined,
): CleFieldValidationState =>
  state.approved === undefined && hasTextValue(courseTitle)
    ? { approved: true, reason: null }
    : state;

export const getCourseCertificateUnmatchedFailingFields = (
  certificate: CourseCertificateEvidenceModel,
) =>
  getUnmatchedFailingFields(
    certificate.fieldValidation,
    COURSE_CERTIFICATE_FIELD_KEYS,
  );

/**
 * Én række til kursets datoer, ikke to.
 *
 * Et bevis bærer kun en slutdato, når kurset løber over flere dage, så et
 * endagskursus har præcis én dato at vise. Rækken hedder derfor "Kursusdato"
 * for et endagskursus og "Kursusperiode", når der er et interval.
 */
export const formatCoursePeriod = (
  certificate: CourseCertificateEvidenceModel,
  formatDate: (value: string | null) => string,
): string => {
  if (!isCourseDateValid(certificate.courseDate)) {
    return "-";
  }
  if (!certificate.courseEndDate) {
    return formatDate(certificate.courseDate);
  }
  return `${formatDate(certificate.courseDate)} - ${formatDate(
    certificate.courseEndDate,
  )}`;
};

export const coursePeriodLabel = (
  certificate: CourseCertificateEvidenceModel,
): string => (certificate.courseEndDate ? "Kursusperiode" : "Kursusdato");

/**
 * Dommen på datorækken. `coursePeriod` vinder, når API'et har afsagt en - den er
 * den strengere af de to, og `courseDate` kan kun fejle på en dato, der slet ikke
 * findes, hvilket netop er det tilfælde, hvor `coursePeriod` udebliver.
 */
export const coursePeriodFieldStatus = (
  status: Record<CourseCertificateFieldKey, CleFieldValidationState>,
): CleFieldValidationState =>
  status.coursePeriod.approved === undefined &&
  status.coursePeriod.reason == null
    ? status.courseDate
    : status.coursePeriod;
